import { PLAYER } from "../../../../common/constants.ts";

export type P3V1LeaderRowLike = {
  userTeam?: boolean;
};

export type P3V1LeaderCategoryLike = {
  leaders: readonly P3V1LeaderRowLike[];
  stat: string;
};

export type P3V1RankedLeader<Category extends P3V1LeaderCategoryLike> = {
  category: Category;
  leader: Category["leaders"][number];
  rank: number;
};

export const getP3V1DefaultLeaderCategories = <Category extends P3V1LeaderCategoryLike>(
  categories: readonly Category[],
  defaultStats: readonly string[],
  limit = defaultStats.length,
) => {
  const defaultStatSet = new Set(defaultStats);
  const defaultCategories = categories.filter((category) => defaultStatSet.has(category.stat));

  return (
    categories.length <= defaultStats.length || defaultCategories.length === 0
      ? categories
      : defaultCategories
  ).slice(0, Math.max(0, limit));
};

export const getP3V1LeaderSpotlight = <Category extends P3V1LeaderCategoryLike>(
  categories: readonly Category[],
): P3V1RankedLeader<Category> | undefined => {
  for (const category of categories) {
    const leader = category.leaders[0];
    if (leader) {
      return {
        category,
        leader,
        rank: 1,
      };
    }
  }
};

export const getP3V1UserTeamHighestLeader = <Category extends P3V1LeaderCategoryLike>(
  categories: readonly Category[],
): P3V1RankedLeader<Category> | undefined => {
  let highestLeader: P3V1RankedLeader<Category> | undefined;

  for (const category of categories) {
    category.leaders.forEach((leader, index) => {
      if (!leader.userTeam) {
        return;
      }

      const rank = index + 1;
      if (!highestLeader || rank < highestLeader.rank) {
        highestLeader = {
          category,
          leader,
          rank,
        };
      }
    });
  }

  return highestLeader;
};

export const getP3V1VisibleRankingRows = <Row>(rows: readonly Row[], limit = 8) =>
  rows.slice(0, Math.max(0, limit));

export type P3V1RatingPlayerLike = {
  ratings: {
    ovr: number;
    pot: number;
  };
  stats: {
    tid: number;
  };
  tid?: number;
};

export const canShowP3V1Rating = (
  player: P3V1RatingPlayerLike,
  challengeNoRatings: boolean,
  retiredTid = PLAYER.RETIRED,
) => !challengeNoRatings || (player.tid ?? player.stats.tid) === retiredTid;

export const getP3V1TopRatingPlayers = <Player extends P3V1RatingPlayerLike>(
  players: readonly Player[],
  challengeNoRatings: boolean,
  limit = 8,
  retiredTid = PLAYER.RETIRED,
) =>
  players
    .map((player, index) => ({
      index,
      player,
      showRatings: canShowP3V1Rating(player, challengeNoRatings, retiredTid),
    }))
    .sort((a, b) => {
      if (a.showRatings !== b.showRatings) {
        return a.showRatings ? -1 : 1;
      }

      if (!a.showRatings && !b.showRatings) {
        return a.index - b.index;
      }

      return (
        b.player.ratings.ovr - a.player.ratings.ovr ||
        b.player.ratings.pot - a.player.ratings.pot ||
        a.index - b.index
      );
    })
    .slice(0, Math.max(0, limit))
    .map((row) => row.player);

export const getP3V1UserTeamRatingMarker = <Player extends P3V1RatingPlayerLike>(
  players: readonly Player[],
  userTid: number,
  challengeNoRatings: boolean,
) => {
  const rankedPlayers = getP3V1TopRatingPlayers(players, challengeNoRatings, players.length);

  const index = rankedPlayers.findIndex((player) => player.stats.tid === userTid);
  if (index < 0) {
    return undefined;
  }

  return {
    player: rankedPlayers[index]!,
    rank: index + 1,
  };
};
